<script setup lang="ts">
import { computed, defineEmits, defineProps, RenderFunction, watch } from 'vue'
import BaseInputWrapper from './BaseInputWrapper.vue'
import { twMerge } from 'tailwind-merge'
import { useFieldHelper } from './useBaseInput'

const props = withDefaults(
    defineProps<{
        modelValue: string | null
        label: string
        labelFontBold?: boolean
        name: string
        type?: string
        readonly?: boolean
        icon?: RenderFunction | null
        required?: boolean
        required2?: boolean
        rules?: any | null
        class?: string
        standalone?: boolean
    }>(),
    {
        type: 'text',
        icon: null,
        rules: null,
        readonly: false,
        class: '',
    }
)

const { errorMessage, handleChange } = useFieldHelper(props.name, props.rules, {
    standalone: props.standalone ? props.standalone : false,
    initialValue: props.modelValue,
})

const emit = defineEmits(['update:modelValue'])

watch(
    () => props.modelValue,
    (value) => {
        handleChange(value)
    }
)

function updateValue(value) {
    handleChange(value)
    emit('update:modelValue', value)
}

const value = computed({
    get: () => props.modelValue,
    set: (value) => {
        updateValue(value)
    },
})

const iconClasses = computed(() => {
    return props.icon ? 'pl-10' : ''
})

const errorMessageClasses = computed(() => {
    return errorMessage.value
        ? 'border-red-300 pr-10 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500'
        : ''
})
</script>

<template>
    <BaseInputWrapper
        :name="props.name"
        :label="props.label"
        :label-font-bold="labelFontBold"
        :icon="icon"
        :required="required"
        :required2="required2"
        :standalone="standalone">
        <input
            :id="props.name"
            v-bind="$attrs"
            v-model="value"
            :type="props.type"
            :name="props.name"
            :readonly="props.readonly"
            :class="
                twMerge(
                    'block w-full rounded-md border-gray-300 py-3 shadow-sm focus:border focus:border-primary focus:ring-primary disabled:bg-gray-100 sm:text-sm',
                    iconClasses,
                    errorMessageClasses,
                    props.class
                )
            "
            :aria-invalid="!!errorMessage" />
        <template #add-on>
            <slot name="add-on" />
        </template>
    </BaseInputWrapper>
</template>
